import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useApiCaller } from '../../utils/ApiUtilities';
import {
    getFavoriteQuestionDeleteUrl,
    getFavoriteQuestionPostUrl,
    getFavoriteQuestionStatusUrl,
    getPracticeCountUrl,
    getPracticeRecordUrl,
    getPublicGradeUrl,
    getPublicPracticeQuestionsUrl,
    getPublicSubjectsUrl,
    getAnonLimitsUrl,
} from '../../utils/UrlUtilities';
import {
    constructFavoriteQuestionStatusPayload,
    constructPracticeSubmissionPayload,
    constructPublicGradePayload,
    getFavoriteQuestionPostPayload,
} from '../../utils/PayloadUtils';
import {
    addAnonymousPracticeCount,
    addPracticeRevealedId,
    getAnonymousFavorites,
    getAnonymousPracticeCountToday,
    toggleAnonymousFavorite,
} from '../../utils/AnonymousProgress';
import { PRACTICE_ANON_LIMIT } from '../../utils/Constants';
import QuizQuestionCard from '../QuizQuestionCard';
import MathText from '../ui/MathText';
import { buildSelectedOptionsJson } from '../../utils/BasicUtilities';
import { limitReachedMessage } from '../../utils/gatingMessages';
import useEntitlement from '../../utils/useEntitlement';
import { useUser } from '../../contexts/UserContext';
import UpgradeNudge from '../UpgradeNudge';

const PRACTICE_COUNT = 25;


function LandingPractice({ level, bcsTerm = '', prefilteredSubject = '', division = '', unit = '', onActivity }) {
    const CallApi = useApiCaller();
    const { userState } = useUser();
    const { isPremium } = useEntitlement();
    const isLoggedIn = !!userState?.isLoggedIn;

    const [phase, setPhase] = useState('idle'); // idle | in_progress
    const [questions, setQuestions] = useState([]);
    const [selected, setSelected] = useState({});
    const [revealedIds, setRevealedIds] = useState(new Set());
    // Answers are NOT included in the practice listing (they'd be scrapeable); they're
    // fetched on demand from the grade endpoint the first time the user reveals one,
    // then cached here as { [question_id]: { correct_option, answer_explanation } }.
    const [answersById, setAnswersById] = useState({});
    const [revealLoading, setRevealLoading] = useState(false);
    const answersLoadedRef = useRef(false);
    // Collapse the question list (keeping all selections + revealed answers) so the
    // sections below (notice board, etc.) are reachable without scrolling past every question.
    const [collapsed, setCollapsed] = useState(false);
    // Subject picker: the list of subjects for this level (for the Reload dropdown)
    // and the subject the current set is filtered to ('' = all subjects).
    const [subjects, setSubjects] = useState([]);
    const [activeSubject, setActiveSubject] = useState(prefilteredSubject || '');
    const [favorites, setFavorites] = useState(new Set());
    const [loading, setLoading] = useState(false);
    // Anonymous users are capped client-side per session; logged-in users use the
    // plan-based server limit. Each "start / reload" counts as one practice session.
    const [practiceCountToday, setPracticeCountToday] = useState(getAnonymousPracticeCountToday());
    const [loggedInCount, setLoggedInCount] = useState(0);
    const [loggedInLimit, setLoggedInLimit] = useState(null);
    // Anonymous cap comes from the server's tunable LIMIT_PRACTICE_ANON (fallback to constant).
    const [anonLimit, setAnonLimit] = useState(PRACTICE_ANON_LIMIT);

    const dailyLimit = isLoggedIn ? loggedInLimit : anonLimit;
    const usedToday = isLoggedIn ? loggedInCount : practiceCountToday;
    const limitReached = dailyLimit != null && usedToday >= dailyLimit;
    const remaining = dailyLimit == null ? null : Math.max(0, dailyLimit - usedToday);

    // Anonymous users: pull the tunable anon cap so LIMIT_PRACTICE_ANON is respected.
    useEffect(() => {
        if (isLoggedIn) return;
        let cancelled = false;
        (async () => {
            const res = await CallApi({ url: getAnonLimitsUrl(), method: 'GET', token: false });
            if (!cancelled && res.success && res.data?.practice != null) setAnonLimit(res.data.practice);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    // Load today's practice count + plan limit for logged-in users.
    useEffect(() => {
        if (!isLoggedIn) return;
        let cancelled = false;
        (async () => {
            const res = await CallApi({ url: getPracticeCountUrl(), method: 'GET', token: true });
            if (!cancelled && res.success) {
                setLoggedInCount(res.data?.count ?? 0);
                setLoggedInLimit(res.data?.limit ?? null);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]);

    // Load the subject list for the Reload dropdown whenever the level / BCS term changes.
    useEffect(() => {
        if (!level) { setSubjects([]); return undefined; }
        let cancelled = false;
        (async () => {
            const res = await CallApi({ url: getPublicSubjectsUrl(level, bcsTerm || '', { division, unit }), method: 'GET', token: false });
            if (!cancelled && res.success) setSubjects(res.data?.subjects || []);
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [level, bcsTerm, division, unit]);

    // Track the subject the current set was loaded with so we know to refetch
    // when a parent component pushes a new prefilteredSubject (e.g. from the
    // weak-subjects CTA on the report card).
    const loadedSubjectRef = useRef(null);

    const fetchQuestions = async (subject = '') => {
        if (!level || limitReached) return;
        // Count this practice session against the daily cap. Logged-in: server-authoritative
        // (a 402 surfaces the upgrade nudge and aborts). Anonymous: client-side.
        if (isLoggedIn) {
            const rec = await CallApi({
                url: getPracticeRecordUrl(),
                method: 'POST',
                payload: constructPracticeSubmissionPayload(level, 0, 0),
                token: true,
            });
            if (!rec.success) return; // 402 handled centrally as an upgrade nudge
            setLoggedInCount(rec.data?.count ?? (loggedInCount + 1));
            if (rec.data?.limit !== undefined) setLoggedInLimit(rec.data.limit);
        } else {
            setPracticeCountToday(addAnonymousPracticeCount(1));
        }
        setLoading(true);
        const url = getPublicPracticeQuestionsUrl(level, PRACTICE_COUNT, subject, bcsTerm || '', { division, unit });
        const response = await CallApi({ url, method: 'GET', token: false });
        setLoading(false);
        if (!response.success) return;

        const fetched = response.data.questions || [];
        setQuestions(fetched);
        setSelected({});
        setRevealedIds(new Set());
        setAnswersById({});
        answersLoadedRef.current = false;
        setCollapsed(false);
        setActiveSubject(subject);
        loadedSubjectRef.current = subject;
        setPhase('in_progress');

        // Initial favorites state.
        if (fetched.length > 0) {
            if (isLoggedIn) {
                const ids = fetched.map(q => q.question_id);
                const statusRes = await CallApi({
                    url: getFavoriteQuestionStatusUrl(),
                    method: 'POST',
                    payload: constructFavoriteQuestionStatusPayload(ids),
                    token: true,
                });
                if (statusRes.success) {
                    const next = new Set();
                    Object.entries(statusRes.data || {}).forEach(([id, isFav]) => {
                        if (isFav) next.add(Number(id));
                    });
                    setFavorites(next);
                }
            } else {
                setFavorites(getAnonymousFavorites());
            }
        }
    };

    // Auto-fetch when a prefilteredSubject arrives (e.g. report card CTA) and
    // it differs from what we last loaded.
    useEffect(() => {
        if (
            prefilteredSubject &&
            level &&
            loadedSubjectRef.current !== prefilteredSubject
        ) {
            fetchQuestions(prefilteredSubject);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prefilteredSubject, level]);

    // When the level or BCS term changes, reset to idle so the user explicitly
    // re-starts practice with the new selection applied. (Tab switches don't
    // remount, so the set is otherwise stable until reload.)
    useEffect(() => {
        setPhase('idle');
        setQuestions([]);
        setSelected({});
        setRevealedIds(new Set());
        setAnswersById({});
        answersLoadedRef.current = false;
        setCollapsed(false);
        loadedSubjectRef.current = null;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bcsTerm, level]);

    const handleStart = () => fetchQuestions(prefilteredSubject || '');

    const handleSelect = (qid, opt) => {
        setSelected(prev => ({ ...prev, [qid]: opt }));
    };
    // Multi-select MCQ / Sentence Equivalence, Text Completion, TEXT, and Build-a-Sentence —
    // stored in the same answer shapes the full quiz taker uses (see buildSelectedOptionsJson).
    const handleToggle = (qid, opt) => {
        setSelected(prev => {
            const cur = Array.isArray(prev[qid]) ? prev[qid] : [];
            return { ...prev, [qid]: cur.includes(opt) ? cur.filter(o => o !== opt) : [...cur, opt] };
        });
    };
    const handleBlank = (qid, bi, ci) => {
        setSelected(prev => {
            const cur = (prev[qid] && prev[qid].blanks) || {};
            return { ...prev, [qid]: { blanks: { ...cur, [bi]: ci } } };
        });
    };
    const handleText = (qid, text) => setSelected(prev => ({ ...prev, [qid]: { text } }));
    const handleOrder = (qid, order) => setSelected(prev => ({ ...prev, [qid]: { order } }));

    // Load answers for the whole set once (via the grade endpoint) and cache them,
    // so revealing subsequent questions needs no further network calls. Returns
    // false if the fetch failed so the caller can skip revealing.
    const ensureAnswersLoaded = async () => {
        if (answersLoadedRef.current) return true;
        // Grade the WHOLE set — including unanswered questions (empty selection) — so revealing
        // a question the user skipped still returns its correct answer + explanation.
        const merged = {};
        questions.forEach(q => { merged[q.question_id] = selected[q.question_id] ?? ''; });
        const payload = constructPublicGradePayload(buildSelectedOptionsJson(merged));
        setRevealLoading(true);
        const res = await CallApi({ url: getPublicGradeUrl(), method: 'POST', payload, token: false });
        setRevealLoading(false);
        if (!res.success) return false;
        const map = {};
        (res.data?.questions || []).forEach(gq => {
            map[gq.question_id] = {
                correct_option: gq.correct_option,
                correct_text_answer: gq.correct_text_answer,
                answer_explanation: gq.answer_explanation,
                metadata: gq.metadata,
            };
        });
        setAnswersById(map);
        answersLoadedRef.current = true;
        return true;
    };

    const handleRevealOne = async (qid) => {
        const ok = await ensureAnswersLoaded();
        if (!ok) return;
        setRevealedIds(prev => {
            const next = new Set(prev);
            next.add(qid);
            return next;
        });
        // Record the viewed answer (device-wide, distinct) and let the homepage
        // activity box refresh immediately.
        addPracticeRevealedId(qid);
        if (onActivity) onActivity();
    };

    const handleHideOne = (qid) => {
        setRevealedIds(prev => {
            const next = new Set(prev);
            next.delete(qid);
            return next;
        });
    };

    const handleToggleFavorite = async (qid) => {
        if (!isLoggedIn) {
            const next = toggleAnonymousFavorite(qid);
            setFavorites(new Set(next));
            return;
        }
        // Logged-in: optimistic toggle, sync via API.
        const wasFav = favorites.has(qid);
        const optimistic = new Set(favorites);
        if (wasFav) optimistic.delete(qid); else optimistic.add(qid);
        setFavorites(optimistic);

        const url = wasFav
            ? getFavoriteQuestionDeleteUrl([qid])
            : getFavoriteQuestionPostUrl();
        const method = wasFav ? 'DELETE' : 'POST';
        const payload = wasFav ? null : getFavoriteQuestionPostPayload(qid);
        const res = await CallApi({ url, method, payload, token: true });
        if (!res.success) {
            // revert
            setFavorites(favorites);
        }
    };

    if (phase === 'idle') {
        if (limitReached) {
            return (
                <div className="card shadow-sm">
                    <div className="card-body text-center py-5">
                        <h3 className="card-title mb-3">Daily practice limit reached</h3>
                        <p className="text-muted mb-4">
                            {limitReachedMessage({ kind: 'practice', isLoggedIn, isPremium, limit: dailyLimit })}
                        </p>
                        {isLoggedIn ? (
                            <UpgradeNudge suffix="for more Practice Question Sets" />
                        ) : (
                            <>
                                <Link to="/signup" className="nav-pill me-2">Sign Up</Link>
                                <Link to="/login" className="nav-pill">Login</Link>
                            </>
                        )}
                    </div>
                </div>
            );
        }
        return (
            <div className="card shadow-sm">
                <div className="card-body text-center py-5">
                    <h3 className="card-title mb-3">Practice 25 questions</h3>
                    <p className="text-muted mb-4">
                        Self-test mode from level {level || '—'} — answer at your own pace, reveal the answer when you're ready.
                        {prefilteredSubject && (
                            <> Subject: <strong>{prefilteredSubject}</strong>.</>
                        )}
                    </p>
                    {remaining != null && (
                        <p className="mb-3">
                            <span className="badge bg-info-subtle text-dark">
                                {remaining} of {dailyLimit} Practice Question {dailyLimit === 1 ? 'Set' : 'Sets'} left today
                            </span>
                        </p>
                    )}
                    <button
                        className="nav-pill"
                        style={{ padding: '0.6rem 1.6rem', fontSize: '1.05rem', opacity: (!level || loading) ? 0.6 : 1 }}
                        onClick={handleStart}
                        disabled={!level || loading}
                    >
                        {loading ? 'Loading…' : 'Start Practice'}
                    </button>
                    {!level && (
                        <div className="text-danger fw-semibold mt-3">
                            <small>Pick a level above to start.</small>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className="card shadow-sm">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-3 sticky-top bg-white py-2"
                    style={{ top: '76px', borderBottom: '1px solid #eee', zIndex: 5 }}>
                    <strong>
                        Practice — {level}
                        {loadedSubjectRef.current && ` • ${loadedSubjectRef.current}`}
                        {' '}({questions.length} questions)
                        {remaining != null && (
                            <span className="badge bg-info-subtle text-dark ms-2">
                                {remaining} of {dailyLimit} left today
                            </span>
                        )}
                    </strong>
                    <div className="d-flex align-items-center gap-2">
                        <div className="dropdown">
                            <button
                                className="btn btn-outline-secondary btn-sm dropdown-toggle"
                                type="button"
                                data-bs-toggle="dropdown"
                                aria-expanded="false"
                                disabled={loading || limitReached}
                                title={limitReached ? 'Daily practice limit reached' : 'Reload 25 questions — pick a subject or all subjects'}
                            >
                                {loading ? 'Loading…' : 'Reload 25'}
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end" style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                                <li>
                                    <button
                                        className={`dropdown-item${activeSubject === '' ? ' active' : ''}`}
                                        onClick={() => fetchQuestions('')}
                                    >
                                        All subjects
                                    </button>
                                </li>
                                {subjects.length > 0 && <li><hr className="dropdown-divider" /></li>}
                                {subjects.map(s => (
                                    <li key={s}>
                                        <button
                                            className={`dropdown-item${activeSubject === s ? ' active' : ''}`}
                                            onClick={() => fetchQuestions(s)}
                                        >
                                            {s}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <button
                            className="btn btn-sm"
                            style={{ backgroundColor: '#7c3aed', color: '#fff', border: 'none' }}
                            onClick={() => setCollapsed(c => !c)}
                            title={collapsed ? 'Expand questions' : 'Collapse questions'}
                        >
                            {collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                            <span className="ms-1">{collapsed ? 'Expand' : 'Collapse'}</span>
                        </button>
                    </div>
                </div>
                {!collapsed && (
                <div style={{ maxHeight: '65vh', overflowY: 'auto', paddingRight: '0.5rem' }}>
                {questions.map((q, idx) => {
                    const isRevealed = revealedIds.has(q.question_id);
                    const answer = answersById[q.question_id];
                    const isFav = favorites.has(q.question_id);
                    return (
                        <div key={q.question_id} className="mb-3 pb-2 border-bottom">
                            {/* Full question rendering (passage, audio, images, cloze, matching,
                                build-a-sentence, LaTeX) — identical to the quiz taker. Interaction
                                is frozen once the answer is revealed. */}
                            <div style={isRevealed ? { pointerEvents: 'none', opacity: 0.95 } : undefined}>
                                <QuizQuestionCard
                                    question={q}
                                    qIndex={idx}
                                    includePassage
                                    answer={selected[q.question_id]}
                                    isAnswered={selected[q.question_id] != null}
                                    isFavorite={isFav}
                                    onOptionSelect={handleSelect}
                                    onMultiToggle={handleToggle}
                                    onBlankSelect={handleBlank}
                                    onTextChange={handleText}
                                    onOrderChange={handleOrder}
                                    onToggleFavorite={handleToggleFavorite}
                                />
                            </div>
                            {isRevealed && answer && (
                                <div className="alert alert-success py-2 mb-2 kp-read-explanation">
                                    <div>
                                        <strong>Correct answer:</strong>{' '}
                                        {answer.correct_text_answer
                                            ? <MathText text={answer.correct_text_answer} />
                                            : (answer.correct_option
                                                ? <MathText text={answer.correct_option} />
                                                : '—')}
                                    </div>
                                    {answer.answer_explanation && (
                                        <div className="mt-1"><strong>Why:</strong> <MathText text={answer.answer_explanation} /></div>
                                    )}
                                </div>
                            )}
                            {isRevealed ? (
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => handleHideOne(q.question_id)}
                                >
                                    Hide this answer
                                </button>
                            ) : (
                                <button
                                    className="btn btn-sm btn-outline-warning"
                                    onClick={() => handleRevealOne(q.question_id)}
                                    disabled={revealLoading}
                                >
                                    {revealLoading ? 'Loading…' : 'Show this answer'}
                                </button>
                            )}
                        </div>
                    );
                })}
                </div>
                )}
            </div>
        </div>
    );
}

export default LandingPractice;
